import cn from 'classnames';
import type { ComponentChildren, ComponentProps, TargetedEvent } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import type { IconType } from '../../types/icons';
import type { HTMLAttributes, HTMLAttributesWithTracking } from '../../types/utility';

type BaseProps = Omit<HTMLAttributesWithTracking<HTMLButtonElement>, 'size' | 'icon'>;

type Variants = {
  badge?: number | string;
  hierarchy?: 'primary' | 'secondary' | 'tertiary';
  size?: 'x-small' | 'small' | 'medium' | 'large';
  icon?: IconType;
  iconPosition?: 'before' | 'after';
  isLoading?: boolean;
};

type ConditionalChildren =
  | { isIconOnly?: false | never; children: NonNullable<ComponentChildren> }
  | { isIconOnly: true; icon: IconType; children?: never; 'aria-label': string };

type ButtonProps = BaseProps & Variants & ConditionalChildren;

export default function Button({
  badge,
  type = 'button',
  hierarchy = 'primary',
  size = 'medium',
  isIconOnly = false,
  isLoading = false,
  icon,
  iconPosition,
  children,
  className,
  analyticsId,
  ...buttonProps
}: ButtonProps) {
  const classList =
    cn(className, {
      [hierarchy]: hierarchy !== 'primary',
      [size]: size !== 'medium',
      ['icon-after']: iconPosition === 'after',
      ['icon-only']: isIconOnly,
      ['is-loading']: isLoading,
    }) || undefined;

  if (icon) {
    buttonProps['data-icon'] = icon;
  }

  if (isLoading) {
    buttonProps['aria-label'] = 'Loading';
    buttonProps.disabled = true;
  }

  // `type` can only be 'submit' | 'button' | 'reset' based on the Props definition, so disabling warning
  return (
    <button
      /* eslint-disable-next-line react/button-has-type */
      type={type}
      className={classList}
      data-blox-analytics-id={analyticsId}
      data-badge={badge}
      {...buttonProps}
    >
      {!isIconOnly && children}
    </button>
  );
}

export function SecondaryButton(props: ButtonProps) {
  return <Button {...props} hierarchy="secondary" />;
}

export function TertiaryButton(props: ButtonProps) {
  return <Button {...props} hierarchy="tertiary" />;
}

export function UnstyledButton({ className, ...props }: ButtonProps) {
  return <Button {...props} className={cn('unstyled', className)} />;
}

export function LinkButton({ className, ...props }: ButtonProps) {
  return <Button {...props} className={cn('visual-link', className)} />;
}

export function ButtonLink({
  children,
  className,
  hierarchy = 'primary',
  icon,
  iconPosition,
  size = 'medium',
  ...props
}: Omit<HTMLAttributes<HTMLAnchorElement>, 'size'> & Variants & ConditionalChildren) {
  const classList = cn('visual-button', className, {
    [hierarchy]: hierarchy !== 'primary',
    [size]: size !== 'medium',
    ['icon-after']: iconPosition === 'after',
    ['icon-only']: props.isIconOnly,
  });

  if (icon) {
    props['data-icon'] = icon;
  }

  return (
    <a {...props} className={classList}>
      {children}
    </a>
  );
}

export function SecondaryButtonLink({ className, ...props }: ComponentProps<typeof ButtonLink>) {
  const classList = cn('secondary', className);
  return <ButtonLink {...props} className={classList} />;
}

export function TertiaryButtonLink({ className, ...props }: ComponentProps<typeof ButtonLink>) {
  const classList = cn('tertiary', className);
  return <ButtonLink {...props} className={classList} />;
}

export function ToggleButton({
  isPressed,
  children,
  className,
  ...buttonProps
}: BaseProps & { children: NonNullable<ComponentChildren>; isPressed: boolean }) {
  const classList = cn('toggle', className);
  return (
    <Button {...buttonProps} className={classList} aria-pressed={isPressed}>
      {children}
    </Button>
  );
}

interface UseToggleButtonProps {
  isPressed?: boolean;
  onToggle?: (e?: TargetedEvent<HTMLButtonElement, MouseEvent>) => void;
}

ToggleButton.useToggleButton = function ({
  isPressed: passedIsPressed,
  onToggle,
}: UseToggleButtonProps) {
  const [isPressed, setIsPressed] = useState(!!passedIsPressed);

  const handleToggle = useCallback(
    (e?: TargetedEvent<HTMLButtonElement, MouseEvent>) => {
      setIsPressed((prev) => !prev);
      onToggle?.(e);
    },
    [onToggle],
  );

  return { isPressed, handleToggle, setIsPressed };
};

type CopyButtonProps = Omit<ButtonProps, 'children'> &
  (
    | { value: string; relatedTarget?: never }
    | { value?: never; relatedTarget: 'nextElementSibling' | 'previousElementSibling' | string }
  );

export function CopyButton({
  className,
  hierarchy = 'secondary',
  relatedTarget,
  ...props
}: CopyButtonProps) {
  const [label, setLabel] = useState('Copy');

  const handleClick = useCallback((e: TargetedEvent<HTMLButtonElement, MouseEvent>) => {
    const { currentTarget } = e;
    const { relatedTarget } = currentTarget.dataset;
    let textToCopy;

    if (relatedTarget) {
      switch (relatedTarget) {
        case 'nextElementSibling':
          textToCopy = currentTarget.nextElementSibling?.textContent;
          break;
        case 'previousElementSibling':
          textToCopy = currentTarget.previousElementSibling?.textContent;
          break;
        default:
          textToCopy = document.getElementById(relatedTarget)?.textContent;
      }
    } else {
      textToCopy = e.currentTarget.value;
    }

    let updatedLabel;
    try {
      navigator.clipboard.writeText(textToCopy || '');
      updatedLabel = 'Copied';
    } catch {
      updatedLabel = 'There was an error copying';
    }

    setLabel(updatedLabel);

    currentTarget.dispatchEvent(
      new CustomEvent('updatetooltip', { bubbles: true, detail: { tooltipText: updatedLabel } }),
    );

    // reset text back to original
    setTimeout(setLabel, 1000, 'Copy');
  }, []);

  const classList = cn('copy-button', className);

  return (
    <Button
      aria-label={label}
      hierarchy={hierarchy}
      title="Copy"
      {...props}
      className={classList}
      icon="copy"
      data-related-target={relatedTarget}
      isIconOnly
      onClick={handleClick}
    />
  );
}

export function BackLink({ children, className, ...rest }: HTMLAttributes<HTMLAnchorElement>) {
  const classList = cn('back-link', className);
  return (
    <a className={classList} data-icon="chevron-left" {...rest}>
      {children}
    </a>
  );
}

export function PillLink({ children, className, ...rest }: HTMLAttributes<HTMLAnchorElement>) {
  const classList = cn('visual-pill', className);
  return (
    <a className={classList} {...rest}>
      {children}
    </a>
  );
}
